import { Location } from '@cobliteam/common-types';
import { alertHookConfig } from '@cobliteam/hooks/src/useAlertHooks/alertHookConfig';
import type { CurrentAlertHook } from '@cobliteam/hooks/src/useAlertHooks/types';
import useAlertHooksStore from '@cobliteam/hooks/src/useAlertHooks/useAlertHooksStore';
import useCreateHookAlert from '@cobliteam/hooks/src/useAlertHooks/useCreateHookAlert';
import { useRefreshTriggerList } from '@cobliteam/hooks/src/useEntity/useAlerts';
import { useTranslation } from '@cobliteam/i18n';
import { Alert } from '@cobliteam/ui/components';
import { useRef } from 'react';
import AlertHookConfirmLayout from './AlertHookConfirmLayout';
import { trackActivated, trackEditClicked, trackRuleSaved } from './analytics';

interface DialogAlertHookConfirmBlueprintProps {
  current: CurrentAlertHook;
}

/**
 * Step 2 of a blueprint hook: the preview mirrors the blueprint-api template, which creates the rule
 * on "Ativar alerta" (`CreateHookAlert`). "Editar alerta" opens the blueprint create form instead.
 */
const DialogAlertHookConfirmBlueprint = ({ current }: DialogAlertHookConfirmBlueprintProps) => {
  const { t } = useTranslation(['alertHooks']);
  const close = useAlertHooksStore((state) => state.close);
  const markActivated = useAlertHooksStore((state) => state.markActivated);
  const refreshTriggerList = useRefreshTriggerList();
  const { createHookAlert, isPending } = useCreateHookAlert(current.type);

  // Same re-entry guard as the trigger path: `isPending` only flips on the next render.
  const isHandlingActivateRef = useRef(false);

  const config = alertHookConfig[current.type];
  if (config.kind !== 'blueprint') return null;

  const { copy } = config;
  const analytics = { hookType: current.type, dismissKey: config.getDismissKey(current.params), alert_type: config.alertType };

  const handleActivate = async () => {
    if (isHandlingActivateRef.current) return;
    isHandlingActivateRef.current = true;
    try {
      // Loaded on click: the renderer stays out of the initial bundle, where this dialog is mounted.
      const { getBlueprintVersion } = await import('@cobliteam/blueprint-react-renderer');
      await createHookAlert({ alertType: config.eventNameIdentifier, rendererVersion: getBlueprintVersion() });
      Alert.success(t(`alertHooks:${current.type}.confirm.success`));
      trackActivated(analytics);
      trackRuleSaved(config.alertType);
      markActivated(current.type);
      refreshTriggerList();
      close();
    } catch {
      // The error toast comes from the query client's global handler; the modal stays open to retry.
    } finally {
      isHandlingActivateRef.current = false;
    }
  };

  const handleEdit = () => {
    trackEditClicked(analytics);
    window.open(Location.ALERTS_BLUEPRINT_CREATE.replace(':alertType', config.alertType), '_blank', 'noopener,noreferrer');
    close();
  };

  return (
    <AlertHookConfirmLayout
      iconName={copy.confirm.iconName ?? copy.suggestion.iconName}
      iconBg={copy.suggestion.iconBg}
      title={t(copy.confirm.titleKey)}
      description={t(copy.confirm.descriptionKey ?? 'alertHooks:common.confirmDescription')}
      rows={config.buildSummaryRows(current.params)}
      editLabel={t(copy.confirm.editCtaKey ?? 'alertHooks:common.ctaEditBeforeActivate')}
      onEdit={handleEdit}
      onActivate={handleActivate}
      isActivating={isPending}
      onClose={close}
    />
  );
};

export default DialogAlertHookConfirmBlueprint;
